import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'

import { MATERIAL_ICONS } from '../../lib/materialIcons'

export type ProFeatureId = 'wearables' | 'predictive' | 'calendar'

const FEATURE_ICONS: Record<ProFeatureId, string> = {
  wearables: MATERIAL_ICONS.watchScreentime,
  predictive: 'auto_awesome',
  calendar: 'calendar_add_on',
}

function dismissStorageKey(feature: ProFeatureId): string {
  return `kilo_pro_upgrade_dismissed_${feature}`
}

function isDismissed(feature: ProFeatureId): boolean {
  try {
    return localStorage.getItem(dismissStorageKey(feature)) === '1'
  } catch {
    return false
  }
}

type ProUpgradeCardProps = {
  feature: ProFeatureId
  onUpgrade: () => void
  dismissible?: boolean
}

export default function ProUpgradeCard({
  feature,
  onUpgrade,
  dismissible = false,
}: ProUpgradeCardProps) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => dismissible && isDismissed(feature))
  const bullets = t(`coach.proUpgrade.${feature}.bullets`, { returnObjects: true }) as string[]
  const ctaKey = feature === 'calendar' ? 'coach.proUpgrade.upgradeToProPlus' : 'coach.proUpgrade.upgradeToPro'

  function handleDismiss() {
    try {
      localStorage.setItem(dismissStorageKey(feature), '1')
    } catch {
      /* ignore storage failures */
    }
    setDismissed(true)
  }

  if (dismissed) return null

  return (
    <div className="relative rounded-2xl border border-kilo-accent/20 bg-kilo-card px-4 py-[1.1rem]">
      {dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          className={`absolute right-3 top-3 rounded-lg p-1 ${KILO.text.soft} transition-colors hover:bg-kilo-nested hover:text-kilo-text`}
          aria-label={t('coach.proUpgrade.dismiss')}
        >
          <MaterialIcon name="close" size="sm" />
        </button>
      ) : null}

      <div className={`flex items-start gap-3 ${dismissible ? 'pr-8' : ''}`}>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-kilo-accent/10">
          <MaterialIcon name={FEATURE_ICONS[feature]} size="sm" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={KILO.meta}>{t('coach.proUpgrade.proFeature')}</p>
          <h3 className={`mt-1 text-sm font-semibold ${KILO.text.primary}`}>{t(`coach.proUpgrade.${feature}.title`)}</h3>
          <p className={`mt-1 text-xs leading-relaxed ${KILO.text.muted}`}>{t(`coach.proUpgrade.${feature}.description`)}</p>
          <ul className="mt-[0.825rem] space-y-[0.275rem]">
            {bullets.map((item) => (
              <li key={item} className={`flex items-center gap-2 text-xs ${KILO.text.primary}`}>
                <MaterialIcon name="check_circle" size="sm" />
                {item}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onUpgrade}
            className="mt-[1.1rem] w-full rounded-xl bg-kilo-accent py-[0.6875rem] text-sm font-semibold text-black"
          >
            {t(ctaKey)}
          </button>
        </div>
      </div>
    </div>
  )
}
